import { DatadogLogger } from '@clio/frontend-logging';
import env from './env';

const APPLICATION_ID = 'd1502518-5ea7-4ca7-87ff-6525187018af';
const CLIENT_TOKEN = 'puba7ddad0c27dbdf921d78b6c8c881ff8d';

const SERVICE_NAME = 'draft';

type Region = 'us' | 'eu' | 'ca' | 'au';

const VALID_REGIONS: ReadonlySet<string> = new Set(['us', 'eu', 'ca', 'au']);

export const ENABLED_DATADOG_STAGES = [
  'production',
  'production-eu',
  'production-au',
  'production-ca',
] as const;

type EnabledDatadogStage = (typeof ENABLED_DATADOG_STAGES)[number];

const ENABLED_DATADOG_STAGE_SET: ReadonlySet<string> = new Set(
  ENABLED_DATADOG_STAGES,
);

export function isDatadogEnabledStage(
  stage: string,
): stage is EnabledDatadogStage {
  return ENABLED_DATADOG_STAGE_SET.has(stage);
}

export function getReleaseStage(): string {
  const environment = env.reactAppEnvironment || 'development';
  const region = env.reactAppRegion || 'us';
  if (environment === 'production') {
    return region === 'us' ? 'production' : `production-${region}`;
  }
  return environment;
}

export function getSessionSampleRate(environment: string): number {
  return isDatadogEnabledStage(environment) ? 100 : 0;
}

export function sanitizeViewName(pathWithQuery: string): string {
  return pathWithQuery
    .replace(/\?.*$/, '')
    .replace(/#.*$/, '')
    .replace(
      /(?<=\/)[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi,
      ':uuid',
    )
    .replace(/(?<=\/)(\d+)(?=\/|$)/g, ':id');
}

let logger: DatadogLogger | null = null;

export function initDatadogRum(): void {
  if (logger) return;

  if (!APPLICATION_ID || !CLIENT_TOKEN) {
    console.error(
      'Datadog RUM not initialized — missing credentials (APPLICATION_ID or CLIENT_TOKEN empty)',
    );
    return;
  }

  const releaseStage = getReleaseStage();
  if (!isDatadogEnabledStage(releaseStage)) {
    console.error(
      `Datadog RUM not initialized — release stage "${releaseStage}" is disabled (Datadog RUM runs in production only)`,
    );
    return;
  }

  const region = env.reactAppRegion || 'us';
  if (!VALID_REGIONS.has(region)) {
    console.error(`Datadog RUM not initialized — invalid region "${region}"`);
    return;
  }

  try {
    logger = new DatadogLogger({
      applicationId: APPLICATION_ID,
      clientToken: CLIENT_TOKEN,
      service: SERVICE_NAME,
      env: releaseStage,
      region: region as Region,
      version: env.githubSha || 'unknown',
      sessionSampleRate: getSessionSampleRate(releaseStage),
      sessionReplaySampleRate: 0,
      trackViewsManually: true,
    });
  } catch (err) {
    console.error('Error initializing Datadog RUM', err);
    logger = null;
  }
}

export function startRumView(pathWithQuery: string): void {
  if (!logger) return;
  logger.startView({ name: sanitizeViewName(pathWithQuery) });
}

export function setRumUser(userId: string | number | null | undefined): void {
  if (!logger || !userId) return;
  logger.setUser({ id: String(userId) });
}
